"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Image from "@/components/ui/image";
import ayraLogo from "@/assets/ayra-logo-dark.png";

const words = ["Bonjour", "Ciao", "Olà", "やあ", "Hallå", "Guten tag", "হ্যালো", "Hello"];

const opacity = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 0.75,
    transition: { duration: 1, delay: 0.2 },
  },
};
const slideUp = {
  initial: {
    top: 0,
  },
  exit: {
    top: "-100vh",
    transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] as const, delay: 0.2 },
  },
};

interface PreloaderProps {
  onComplete?: () => void;
}

export default function Preloader({ onComplete }: PreloaderProps) {
  const [index, setIndex] = useState(0);
  const [dimension, setDimension] = useState({ width: 0, height: 0 });
  const [isExiting, setIsExiting] = useState(false);
  const [showLogo, setShowLogo] = useState(false);

  useEffect(() => {
    setDimension({ width: window.innerWidth, height: window.innerHeight });
  }, []);

  useEffect(() => {
    if (index === words.length - 1) {
      // Show logo after "Hello" is displayed
      setTimeout(() => {
        setShowLogo(true);
        // Start exit animation after showing logo
        setTimeout(() => {
          setIsExiting(true);
          // Call onComplete after exit animation
          setTimeout(() => {
            onComplete?.();
          }, 1000);
        }, 1500); // Show logo for 1.5 seconds
      }, 1000); // Wait 1 second after "Hello" appears
      return;
    }

    setTimeout(
      () => {
        setIndex(index + 1);
      },
      index === 0 ? 1000 : 150,
    );
  }, [index, onComplete]);

  const initialPath = `M0 0 L${dimension.width} 0 L${dimension.width} ${dimension.height} Q${dimension.width / 2} ${dimension.height + 300} 0 ${dimension.height} L0 0`;
  const targetPath = `M0 0 L${dimension.width} 0 L${dimension.width} ${dimension.height} Q${dimension.width / 2} ${dimension.height} 0 ${dimension.height} L0 0`;

const curve = {
  initial: {
    d: initialPath,
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] as const },
  },
  exit: {
    d: targetPath,
    transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1] as const, delay: 0.3 },
  },
};


  return (
    <motion.div
      variants={slideUp}
      initial="initial"
      animate={isExiting ? "exit" : "initial"}
      className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-white z-[9999]"
    >
      {dimension.width > 0 && (
        <>
          {!showLogo ? (
            <motion.p
              variants={opacity}
              initial="initial"
              animate="enter"
              exit={{ opacity: 0 }}
              className="text-black text-4xl md:text-5xl lg:text-6xl absolute z-10 font-bold"
            >
              {words[index]}
            </motion.p>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              className="absolute z-10 h-16 md:h-20 lg:h-24 w-auto relative"
            >
              <Image
                src={ayraLogo}
                alt="Ayra Logo"
                width={200}
                height={80}
                className="h-full w-auto object-contain"
                priority
              />
            </motion.div>
          )}
          <svg className="absolute top-0 w-full h-[calc(100%+300px)]">
            <motion.path variants={curve} initial="initial" animate={isExiting ? "exit" : "initial"} fill="#ffffff" />
          </svg>
        </>
      )}
    </motion.div>
  );
}



